import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarPlus, Check, Copy, Dumbbell, Loader2, X } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

/* P1 Coaching (12.10.2026): Verkauf und Pflege eines Pakets — Karte in der Lead-Ansicht und Baustein der Seite /coaching.
   Preise, Anrechnung und Kontingent kommen vom Server; hier wird nichts gerechnet. */

const euro = (v) => `${Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const tagDe = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '');
const fehler = (e, rest) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const STATUS = {
  offen: ['wartet auf Zahlung', 'outline'], bezahlt: ['bezahlt · Starttermin fehlt', 'secondary'], laufend: ['läuft', 'default'],
  abgeschlossen: ['abgeschlossen', 'secondary'], verfallen: ['verfallen', 'outline'], storniert: ['storniert', 'outline'],
  erstattet: ['erstattet', 'outline'], erstattung_laeuft: ['Erstattung läuft', 'outline'],
};
const feld = 'h-9 rounded-md border border-input bg-background px-2 text-sm';

/* Eine Rückfrage mit Begründung — im Blatt selbst, kein Browser-Dialog. */
function MitGrund({ text, knopf, onOk, onAbbruch, mitBetrag }) {
  const [grund, setGrund] = useState('');
  const [betrag, setBetrag] = useState('');
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
      <p className="text-sm">{text}</p>
      <Input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Begründung (mindestens 5 Zeichen)" maxLength={300} aria-label="Begründung" />
      {mitBetrag && <Input value={betrag} onChange={(e) => setBetrag(e.target.value)} placeholder="Betrag in € (leer = alles)" inputMode="decimal" aria-label="Betrag" className="w-48" />}
      <div className="flex gap-2">
        <Button size="sm" disabled={grund.trim().length < 5} onClick={() => onOk(grund.trim(), betrag ? Number(betrag.replace(',', '.')) : null)}>{knopf}</Button>
        <Button size="sm" variant="ghost" onClick={onAbbruch}>Abbrechen</Button>
      </div>
    </div>
  );
}

export function PaketKarte({ paket: start, trainer = [], rolle, onGeaendert, mitName }) {
  const [p, setP] = useState(start);
  const [busy, setBusy] = useState(false);
  const [neu, setNeu] = useState(null);            // { art, datum, start }
  const [umzug, setUmzug] = useState(null);        // { id, datum, start }
  const [frage, setFrage] = useState(null);        // verlaengern | stornieren | erstatten | garantie
  const [werte, setWerte] = useState(null);
  useEffect(() => { setP(start); }, [start]);
  const leitung = rolle === 'admin' || rolle === 'manager';
  const lebt = ['bezahlt', 'laufend'].includes(p.status);

  const tu = async (aufruf, ok) => {
    setBusy(true);
    try { const r = await aufruf(); if (r?.data?.paket) setP(r.data.paket); if (ok) toast.success(ok); onGeaendert?.(); return true; }
    catch (e) { toast.error(fehler(e, 'Das hat nicht geklappt')); return false; }
    finally { setBusy(false); }
  };
  const [text, variante] = STATUS[p.status] || [p.status, 'outline'];
  const stand = p.garantie?.stand;

  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid={`coaching-paket-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold break-words">{mitName && <span>{p.lead_name} · </span>}{p.name}</p>
          <p className="text-xs text-muted-foreground">
            {euro(p.preis)}{p.angerechnet > 0 && ` (Plan-Check ${euro(p.angerechnet)} angerechnet)`} · {p.zahlung === 'beitragseinzug' ? `Beitragseinzug${p.raten > 1 ? `, ${p.raten} Raten` : ''}` : p.zahlung === 'garantie' ? 'Garantie' : 'Stripe'}
            {p.gym_inklusive && ' · mit Gym & Sauna'}
            {p.beginn && ` · ${tagDe(p.beginn)} bis ${tagDe(p.gueltig_bis)}`}{p.verlaengert && ' (verlängert)'}
          </p>
        </div>
        <Badge variant={variante}>{text}</Badge>
      </div>

      {p.status === 'offen' && p.link && (
        <div className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 p-2 text-sm">
          <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">{p.link}</span>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={async () => { try { await navigator.clipboard.writeText(p.link); toast.success('Zahlungslink kopiert'); } catch { toast.error('Bitte von Hand kopieren'); } }}>
            <Copy className="h-3.5 w-3.5" /> Zahlungslink kopieren
          </Button>
        </div>
      )}

      {lebt && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">Coach</span>
          <select className={feld} aria-label="Coach" value={p.coach?.link_id || ''} disabled={busy}
            onChange={(e) => e.target.value && tu(() => api.put(`/coaching/pakete/${p.id}/coach`, { trainer_link_id: e.target.value }), 'Coach eingetragen')}>
            <option value="">{p.coach?.name || 'noch offen'}</option>
            {trainer.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <span className="text-xs text-muted-foreground">{p.termine_wahrgenommen} von {p.termine_soll} Terminen wahrgenommen{p.tage_rest != null && p.status === 'laufend' ? ` · noch ${Math.max(p.tage_rest, 0)} Tage` : ''}</span>
        </div>
      )}

      {(p.termine || []).length > 0 && (
        <ul className="divide-y divide-border/60 text-sm">
          {p.termine.map((t) => (
            <li key={t.termin_id} className="py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{t.art === 'check' ? 'Abschluss-Check' : `Termin ${t.nr}`}</span>
                <span className="text-muted-foreground">{tagDe(t.am)}, {t.start}–{t.ende} · {t.coach}</span>
                <Badge variant="outline" className="text-[10px]">{t.status}{t.spaet_abgesagt ? ' (spät abgesagt)' : ''}</Badge>
                {t.status === 'geplant' && lebt && (
                  <span className="ml-auto flex gap-1">
                    <Button size="sm" variant="ghost" className="h-7 gap-1" disabled={busy} onClick={() => tu(() => api.patch(`/coaching/pakete/${p.id}/termine/${t.termin_id}`, { status: 'wahrgenommen' }), 'Als wahrgenommen eingetragen')}><Check className="h-3.5 w-3.5" /> war da</Button>
                    <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={() => setUmzug({ id: t.termin_id, datum: t.am, start: t.start })}>verlegen</Button>
                    <Button size="sm" variant="ghost" className="h-7 gap-1" disabled={busy} onClick={() => tu(() => api.patch(`/coaching/pakete/${p.id}/termine/${t.termin_id}`, { status: 'abgesagt' }), 'Absage eingetragen')}><X className="h-3.5 w-3.5" /> absagen</Button>
                  </span>
                )}
              </div>
              {umzug?.id === t.termin_id && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <input type="date" className={feld} aria-label="Neues Datum" value={umzug.datum} onChange={(e) => setUmzug({ ...umzug, datum: e.target.value })} />
                  <input type="time" className={feld} aria-label="Neue Uhrzeit" value={umzug.start} onChange={(e) => setUmzug({ ...umzug, start: e.target.value })} />
                  <Button size="sm" disabled={busy} onClick={async () => { if (await tu(() => api.post(`/coaching/pakete/${p.id}/termine/${t.termin_id}/verschieben`, { datum: umzug.datum, start: umzug.start }), 'Termin verlegt')) setUmzug(null); }}>Verlegen</Button>
                  <Button size="sm" variant="ghost" onClick={() => setUmzug(null)}>Abbrechen</Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {lebt && <p className="text-[11px] text-muted-foreground">Absage später als 24 Stunden vor dem Termin zählt als wahrgenommen.</p>}

      {lebt && !neu && (
        <div className="flex flex-wrap gap-2">
          {p.termine_offen > 0 && <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setNeu({ art: 'termin', datum: '', start: '' })}><CalendarPlus className="h-3.5 w-3.5" /> Termin eintragen</Button>}
          {p.abschluss_check && !p.check_gebucht && <Button size="sm" variant="outline" onClick={() => setNeu({ art: 'check', datum: '', start: '' })}>Abschluss-Check eintragen</Button>}
          {p.beginn && (
            <Button size="sm" variant="ghost" onClick={async () => {
              try { const r = await api.get(`/coaching/pakete/${p.id}/bericht`, { params: { format: 'pdf' }, responseType: 'blob' }); window.open(URL.createObjectURL(r.data), '_blank', 'noopener'); }
              catch { toast.error('Bericht nicht erstellt'); }
            }} data-testid={`coaching-bericht-${p.id}`}>Abschlussbericht (PDF)</Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setWerte((p.testwerte || []).length ? p.testwerte.map((w) => ({ ...w })) : [{ name: '' }, { name: '' }, { name: '' }])}>Testwerte</Button>
        </div>
      )}
      {neu && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
          <span className="text-sm font-medium">{neu.art === 'check' ? 'Abschluss-Check (15 Min.)' : 'Coach-Termin (60 Min.)'}</span>
          <input type="date" className={feld} aria-label="Datum" value={neu.datum} onChange={(e) => setNeu({ ...neu, datum: e.target.value })} />
          <input type="time" className={feld} aria-label="Uhrzeit" value={neu.start} onChange={(e) => setNeu({ ...neu, start: e.target.value })} />
          <Button size="sm" disabled={busy || !neu.datum || !neu.start} onClick={async () => { if (await tu(() => api.post(`/coaching/pakete/${p.id}/termine`, { datum: neu.datum, start: neu.start, art: neu.art }), 'Termin eingetragen')) setNeu(null); }}>Eintragen</Button>
          <Button size="sm" variant="ghost" onClick={() => setNeu(null)}>Abbrechen</Button>
        </div>
      )}

      {werte ? (
        <div className="space-y-2 rounded-md border border-border p-2">
          <p className="text-sm font-medium">Testwerte (Start · Zwischentermin · Abschluss)</p>
          {werte.map((w, i) => (
            <div key={i} className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
              {['name', 'start', 'zwischen', 'ende'].map((k) => (
                <Input key={k} className="h-8" value={w[k] || ''} aria-label={`Testwert ${i + 1} ${k}`} placeholder={{ name: 'z. B. Kniebeuge', start: 'Start', zwischen: 'Woche 5', ende: 'Abschluss' }[k]}
                  onChange={(e) => setWerte(werte.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} />
              ))}
            </div>
          ))}
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} onClick={async () => { if (await tu(() => api.put(`/coaching/pakete/${p.id}/testwerte`, { testwerte: werte.filter((w) => (w.name || '').trim()) }), 'Testwerte gespeichert')) setWerte(null); }}>Speichern</Button>
            <Button size="sm" variant="ghost" onClick={() => setWerte(null)}>Abbrechen</Button>
          </div>
        </div>
      ) : (p.testwerte || []).length > 0 && (
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="font-normal py-1">Testwert</th><th className="font-normal">Start</th><th className="font-normal">Woche 5</th><th className="font-normal">Abschluss</th></tr></thead>
          <tbody>{p.testwerte.map((w) => <tr key={w.name} className="border-t border-border/50"><td className="py-1 font-medium">{w.name}</td><td>{w.start || '–'}</td><td>{w.zwischen || '–'}</td><td>{w.ende || '–'}</td></tr>)}</tbody>
        </table>
      )}

      {stand && (
        <p className="text-xs text-muted-foreground">Garantie-Quote: <strong className="text-foreground">{stand.ist} von {stand.soll_gesamt} Einheiten ({stand.quote} %)</strong> · bisher fällig {stand.soll_bisher} · {stand.erfuellt ? '80 % erreicht' : '80 % noch nicht erreicht'}</p>
      )}
      {p.garantie?.eingeloest && <p className="text-xs text-muted-foreground">Fortschritts-Garantie eingelöst.</p>}

      {!frage ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
          {leitung && p.status === 'laufend' && !p.verlaengert && <button type="button" className="underline text-muted-foreground hover:text-foreground" onClick={() => setFrage('verlaengern')}>um 14 Tage verlängern</button>}
          {(p.status === 'offen' || (lebt && p.zahlung !== 'stripe' && p.termine_wahrgenommen === 0)) && <button type="button" className="underline text-muted-foreground hover:text-foreground" onClick={() => setFrage('stornieren')}>stornieren</button>}
          {rolle === 'admin' && lebt && p.zahlung === 'stripe' && <button type="button" className="underline text-muted-foreground hover:text-foreground" onClick={() => setFrage('erstatten')}>erstatten</button>}
          {leitung && ['laufend', 'abgeschlossen', 'verfallen'].includes(p.status) && !p.garantie?.eingeloest && p.zahlung !== 'garantie' && <button type="button" className="underline text-muted-foreground hover:text-foreground" onClick={() => setFrage('garantie')}>Garantie einlösen</button>}
        </div>
      ) : (
        <MitGrund mitBetrag={frage === 'erstatten'} onAbbruch={() => setFrage(null)}
          text={{ verlaengern: 'Einmalig um 14 Tage verlängern (z. B. Krankheit).', stornieren: 'Paket stornieren. Geplante Termine werden abgesagt.', erstatten: 'Geld über Stripe zurückzahlen. Das Paket ist danach beendet.', garantie: 'Fortschritts-Garantie einlösen: vier Wochen Coaching ohne Berechnung als neues Paket.' }[frage]}
          knopf={{ verlaengern: 'Verlängern', stornieren: 'Stornieren', erstatten: 'Erstatten', garantie: 'Einlösen' }[frage]}
          onOk={async (grund, betrag) => { if (await tu(() => api.post(`/coaching/pakete/${p.id}/${frage}`, { grund, betrag }), 'Erledigt')) setFrage(null); }} />
      )}
    </div>
  );
}

export default function CoachingKarte({ leadId, rolle }) {
  const [angebot, setAngebot] = useState(null);
  const [pakete, setPakete] = useState([]);
  const [trainer, setTrainer] = useState([]);
  const [wahl, setWahl] = useState('');
  const [raten, setRaten] = useState(1);
  const [zahlung, setZahlung] = useState('');
  const [busy, setBusy] = useState(false);
  const [offen, setOffen] = useState(false);

  const lade = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([api.get('/coaching/produkte', { params: { lead_id: leadId } }), api.get('/coaching/pakete', { params: { lead_id: leadId } })]);
      setAngebot(a.data); setTrainer(p.data.trainer || []);
      const voll = await Promise.all((p.data.pakete || []).map((x) => api.get(`/coaching/pakete/${x.id}`).then((r) => r.data.paket).catch(() => x)));
      setPakete(voll);
    } catch { setAngebot(null); }
  }, [leadId]);
  useEffect(() => { lade(); }, [lade]);
  if (!angebot) return null;

  const produkt = angebot.produkte.find((x) => x.schluessel === wahl);
  const verkaufen = async () => {
    setBusy(true);
    try {
      const r = await api.post('/coaching/verkaufen', { lead_id: leadId, produkt: wahl, raten, zahlung: zahlung || undefined });
      toast.success(r.data.paket.status === 'offen' ? 'Zahlungslink erzeugt — bitte an den Kunden geben' : 'Verkauft. Der Betrag läuft über den Beitragseinzug.');
      setWahl(''); setRaten(1); setZahlung(''); setOffen(false); lade();
    } catch (e) { toast.error(fehler(e, 'Verkauf nicht möglich')); }
    setBusy(false);
  };

  return (
    <Card data-testid="coaching-karte">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex flex-wrap items-center gap-2">
          <Dumbbell className="h-4 w-4 text-marke" /> P1 Coaching
          <span className="text-xs font-normal text-muted-foreground">{angebot.mitglied ? 'Mitgliederpreise' : 'Preise für Nicht-Mitglieder, mit Gym & Sauna'} · {angebot.kontingent.frei} von {angebot.kontingent.plaetze} Plätzen frei</span>
          <Button size="sm" variant={offen ? 'ghost' : 'outline'} className="ml-auto" onClick={() => setOffen((v) => !v)}>{offen ? 'Schließen' : 'Paket verkaufen'}</Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {offen && (
          <div className="space-y-3 rounded-xl border border-border p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              {angebot.produkte.map((x) => (
                <button key={x.schluessel} type="button" aria-pressed={wahl === x.schluessel} onClick={() => { setWahl(x.schluessel); setRaten(1); setZahlung(''); }}
                  className={`rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${wahl === x.schluessel ? 'border-marke bg-marke/5' : 'border-border hover:bg-muted/50'}`}>
                  <span className="flex items-baseline justify-between gap-2"><span className="font-semibold">{x.titel}</span><span className="font-bold tabular-nums">{euro(x.zu_zahlen)}</span></span>
                  <span className="block text-xs text-muted-foreground">{x.kurz}{x.angerechnet > 0 && ` · statt ${euro(x.preis)}, Plan-Check angerechnet`}</span>
                </button>
              ))}
            </div>
            {produkt && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                {angebot.mitglied ? (
                  <>
                    <select className={feld} aria-label="Zahlung" value={zahlung || 'beitragseinzug'} onChange={(e) => { setZahlung(e.target.value); setRaten(1); }}>
                      <option value="beitragseinzug">über den Beitragseinzug</option><option value="stripe">Zahlungslink (Stripe)</option>
                    </select>
                    {produkt.raten_moeglich && (zahlung || 'beitragseinzug') === 'beitragseinzug' && (
                      <select className={feld} aria-label="Raten" value={raten} onChange={(e) => setRaten(Number(e.target.value))}>
                        <option value={1}>einmalig {euro(produkt.zu_zahlen)}</option><option value={2}>2 Raten je {euro(produkt.zu_zahlen / 2)}</option>
                      </select>
                    )}
                  </>
                ) : <span className="text-muted-foreground">Zahlung über einen Stripe-Link (24 Stunden gültig).</span>}
                <Button size="sm" disabled={busy || angebot.kontingent.frei < 1} onClick={verkaufen}>{busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}Verkaufen</Button>
                {angebot.kontingent.frei < 1 && <span className="text-xs text-destructive">Ausgebucht — bitte auf die Warteliste setzen.</span>}
              </div>
            )}
            {angebot.einstieg_hinweis && <p className="text-xs text-muted-foreground">{angebot.einstieg_hinweis}</p>}
            <p className="text-[11px] text-muted-foreground">Es gelten die Besonderen Bedingungen für das P1 Coaching (Fassung {angebot.bedingungen_fassung}).</p>
          </div>
        )}
        {pakete.length === 0 && !offen && <p className="text-sm text-muted-foreground">Noch kein Coaching-Paket.</p>}
        {pakete.map((x) => <PaketKarte key={x.id} paket={x} trainer={trainer} rolle={rolle} onGeaendert={lade} />)}
      </CardContent>
    </Card>
  );
}
